import "#/shared/api/api-host-global.js";
import "./sparkle_namespace.js";

(function () {
  if (window.Sparkle.Codeload) {
    return;
  }

  class Codeloader {
    constructor({ cssContainer = "head", jsContainer = "body", prefetchContainer } = {}) {
      this.registry = {};
      this.dependencyGroups = {};
      this.cssContainer = cssContainer;
      this.jsContainer = jsContainer;
      this.prefetchContainer = prefetchContainer || cssContainer;
    }

    // Load a manifest of { asset: condition, ... } pairs. A promise is returned which resolves when all the assets have been imported.
    // NOTE: Javascripts are downloaded asynchronously but executed in the order
    importManifest(manifest) {
      var dependencyGroupId = this.#nextDependencyGroupId();
      var imports = jQuery.map(manifest, (condition, assetUrl) => {
        return this.importIf(condition, assetUrl, { dependencyGroupId: dependencyGroupId });
      });

      // Use .done() instead of jQuery.when to avoid jQuery 3.x issue where
      // .when() with already-resolved deferreds from module script loads
      // never resolves.
      var combined = new jQuery.Deferred();
      var remaining = imports.length;
      if (remaining === 0) {
        combined.resolve();
        return combined.promise();
      }
      imports.forEach((promise) => {
        promise.done(() => {
          if (--remaining === 0) {
            combined.resolve();
          }
        });
        promise.fail((err) => {
          combined.reject(err);
        });
      });
      return combined.promise();
    }

    // NOTE: Javascripts are downloaded asynchronously but executed in the order
    importAllIf(condition, assetList) {
      return condition ? this.importAll(assetList) : this.#createResolvedPromise();
    }

    // NOTE: Javascripts are downloaded asynchronously but executed in the order
    importAll(assetList) {
      var dependencyGroupId = this.#nextDependencyGroupId();
      var imports = jQuery.map(assetList, (assetUrl) => {
        return this.import(assetUrl, { dependencyGroupId: dependencyGroupId });
      });

      var combined = new jQuery.Deferred();
      var remaining = imports.length;
      if (remaining === 0) {
        combined.resolve();
        return combined.promise();
      }
      imports.forEach((promise) => {
        promise.done(() => {
          if (--remaining === 0) {
            combined.resolve();
          }
        });
        promise.fail((err) => {
          combined.reject(err);
        });
      });
      return combined.promise();
    }

    importIf(condition, assetUrl, options) {
      return condition ? this.import(assetUrl, options) : this.#createResolvedPromise();
    }

    import(assetUrl, options) {
      // Skip when no URL is provided — happens during the Propshaft → Vite
      // transition when an asset path config key resolves to nil because
      // the asset has been moved to Vite and isn't in Propshaft's load path.
      if (!assetUrl) {
        return this.#createResolvedPromise();
      }
      assetUrl = this.#resolvedAssetUrl(assetUrl);
      var isExternal = this.#isExternalUrl(assetUrl);
      options = jQuery.extend({}, options);

      if (!options.type) {
        options.type = this.#detectType(assetUrl);
      }

      var asset = this.#lookup(assetUrl);
      if (asset && options.type == "js") {
        // Register the asset for the dependencyGroupId to ensure the code executes in the correct order per group
        this.#register(asset.url, options.dependencyGroupId);
      } else if (asset && options.type == "css") {
        // dependencyGroupId is not implemented for CSS load yet, so there is nothing to do if the asset is already registered
      } else if (options.type == "js") {
        asset = this.#importJS(assetUrl, options.dependencyGroupId, isExternal);
      } else if (options.type == "css") {
        asset = this.#importCSS(assetUrl);
      }

      return asset.deferred.promise();
    }

    // Download assets needed on this page without executing scripts or applying styles.
    // Keep hints outside the import registry: a later import must still execute each asset.
    preloadAll(assetList) {
      assetList.forEach((assetUrl) => {
        jQuery("<link>", this.#preloadOptions(this.#resolvedAssetUrl(assetUrl))).appendTo(this.prefetchContainer);
      });
    }

    prefetchAll(assetList) {
      assetList.forEach((assetUrl) => {
        this.prefetch(assetUrl);
      }, this);
    }

    // Tell the browser to prefetch the given asset that may be used later
    prefetch(assetUrl) {
      // Skip when no URL is provided — happens during the Propshaft → Vite
      // transition when an asset path config key resolves to nil because
      // the asset has been moved to Vite and isn't in Propshaft's load path.
      // Mirrors the same guard in `import()`.
      if (!assetUrl) {
        return;
      }
      jQuery(this.prefetchContainer).append(
        jQuery("<link>", { rel: "prefetch", href: this.#resolvedAssetUrl(assetUrl) }),
      );
    }

    // HELPER FUNCTIONS

    // Sparkle assets live on the page origin or the API host (embeds); anything
    // else is a third-party script that no Vite bundle can contain.
    #isExternalUrl(resolvedUrl) {
      try {
        var origin = new URL(resolvedUrl).origin;
        return (
          origin !== window.location.origin && origin !== new URL(window.Sparkle.apiHost, window.location.href).origin
        );
      } catch (e) {
        return false;
      }
    }

    #resolvedAssetUrl(assetUrl) {
      const isFullyQualifiedURL = assetUrl.startsWith("http");

      if (isFullyQualifiedURL) {
        return assetUrl;
      } else {
        return new URL(assetUrl, window.Sparkle.apiHost).href;
      }
    }

    #nextDependencyGroupId() {
      return Object.keys(this.dependencyGroups).length + 1; // Add 1 so we don't return a falsey 0 value could be misinterpreted later
    }

    #importJS(assetUrl, dependencyGroupId, isExternal) {
      var asset = this.#register(assetUrl, dependencyGroupId);

      if (isExternal) {
        // Third-party scripts (recaptcha, Twilio CDN, …) are not part of any
        // Vite bundle — load them for real via a script tag.
        this.#loadJSAsset(asset);
        return asset;
      }

      // App-relative JS is bundled by Vite — immediately resolve.
      // The script is already loaded as part of the entrypoint bundle.
      asset.preloaded = true;
      asset.loaded = true;
      asset.deferred.resolve();

      return asset;
    }

    #preloadSupported() {
      // SOURCE: https://medium.com/reloading/preload-prefetch-and-priorities-in-chrome-776165961bbf
      var relList = document.createElement("link").relList;

      if (!relList || !relList.supports) {
        return false;
      } else {
        return relList.supports("preload");
      }
    }

    #preloadNatively(asset) {
      var deferred = new jQuery.Deferred();

      jQuery("<link>", this.#preloadOptions(asset.url))
        .on("load", () => {
          asset.preloaded = true;
          deferred.resolve();
        })
        .on("error", (err) => {
          console.error("Error preloading asset: " + asset.url, err);
        })
        .appendTo(this.prefetchContainer);

      return deferred.promise();
    }

    #preloadOptions(url) {
      if (url.endsWith(".module.js")) {
        return { rel: "modulepreload", href: url.replace(/\.module\.js$/, ".js") };
      }

      return { rel: "preload", href: url, as: this.#detectType(url) === "css" ? "style" : "script" };
    }

    // For browsers that don't support preload. We preload with jQuery, which may incur some processing as the browser
    // doesn't know we only want to cache the response.
    #preloadLegacy(asset) {
      return jQuery.ajax({ url: asset.url, dataType: "text", cache: true }).then(() => {
        asset.preloaded = true;
      });
    }

    // Given an asset, find the next dependency in each of its dependency groups, and see if we can load it yet
    #checkDependencyGroups(asset) {
      jQuery.each(asset.dependencyGroups, (_, groupAssets) => {
        jQuery.each(groupAssets, (_, groupAsset) => {
          if (!groupAsset.loaded) {
            this.#loadJSAsset(groupAsset).then(() => {
              this.#checkDependencyGroups(groupAsset);
            });

            return false; // Stop checking because we have found the next candidate but it is not preloaded yet
          }
        });
      });
    }

    #loadJSAsset(asset) {
      if (!asset.loading) {
        asset.loading = true;

        var type = "text/javascript";
        var url = asset.url;

        if (url.endsWith(".module.js")) {
          type = "module";
          url = url.replace(/\.module\.js$/, ".js");
        }

        jQuery("<script>", { type: type })
          .on("load", () => {
            asset.loaded = true;
            asset.loading = false;
            asset.deferred.resolve();
          })
          .appendTo(this.jsContainer)
          .attr("src", url); // Set the src after the handlers have been added or they won't work
      }

      return asset.deferred.promise();
    }

    #importCSS(assetUrl) {
      var asset = this.#register(assetUrl);

      jQuery("<link>", { rel: "stylesheet", type: "text/css", href: assetUrl })
        .on("load", () => {
          asset.loaded = true;
          asset.deferred.resolve();
        })
        .appendTo(this.cssContainer);

      return asset;
    }

    #lookup(assetUrl) {
      return this.registry[assetUrl];
    }

    #register(assetUrl, dependencyGroupId) {
      var asset = this.registry[assetUrl] || {
        url: assetUrl,
        dependencyGroups: [],
        deferred: new jQuery.Deferred(),
        preloaded: false,
        loaded: false,
      };
      this.registry[assetUrl] = asset;

      if (dependencyGroupId) {
        var group = this.dependencyGroups[dependencyGroupId] || [];
        group.push(asset);
        asset.dependencyGroups.push(group);
        this.dependencyGroups[dependencyGroupId] = group;
      }

      return asset;
    }

    #createResolvedPromise() {
      return new jQuery.Deferred().resolve().promise();
    }

    #detectType(assetUrl) {
      if (/\.css$/.test(assetUrl)) {
        return "css";
      } else {
        return "js";
      }
    }
  }

  // INITIALIZE

  window.Sparkle.Codeload = new Codeloader();

  // Creates a custom instance of the codeloader for use in isolated contexts like the Shadow DOM.
  window.Sparkle.Codeload.custom = (...args) => {
    return new Codeloader(...args);
  };
})();
